import { FileDropzone, type FileUploadRejection } from '@heidi/design-system/file-upload';
import {
  TypographyCaption,
  TypographyH3,
  TypographyP2,
} from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { ArrowUpToLine } from 'lucide-react';
import { USER_DOCUMENTS_ACCEPT_FORMATS } from '../../../contracts';
import { SourceSearchInput } from '../source-lists/SourceSearchInput';
import { createLibraryUploadFile, type LibraryUploadFile } from '../upload.types';

interface EntryStepProps {
  /** Called when user clicks a source from the search dropdown */
  onSelectSource: (domain: string) => void;
  /** Called when user drops/selects files */
  onFilesSelected: (files: LibraryUploadFile[]) => void;
  /** Called when the dropzone rejects a file (invalid type, too large, too many). */
  onFilesRejected: (rejections: FileUploadRejection[]) => void;
  /** Error message to display (e.g. file validation error) */
  error: string | null;
}

/**
 * Step 1 of the Create Collection Wizard.
 * Presents a search input for web sources and a drag-and-drop area for documents.
 * User action determines which path (web sources vs documents) the wizard takes.
 */
export const EntryStep = ({
  onSelectSource,
  onFilesSelected,
  onFilesRejected,
  error,
}: EntryStepProps) => {
  const { t } = useTranslations();

  const handleFilesAccepted = (files: File[]) => {
    onFilesSelected(files.map(createLibraryUploadFile));
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <TypographyH3>{t('library.wizard.title' as TranslationKey)}</TypographyH3>

      {/* Search for web sources */}
      <div className="flex flex-col gap-2">
        <SourceSearchInput
          onSelectSource={onSelectSource}
          placeholder={t('library.wizard.entry.searchPlaceholder' as TranslationKey)}
          autoFocus
        />
      </div>

      {/* Divider */}
      <TypographyCaption className="text-center text-text-tertiary">
        {t('library.wizard.entry.divider' as TranslationKey)}
      </TypographyCaption>

      {/* File upload area — grows to fill remaining space */}
      <FileDropzone
        multiple
        accept={USER_DOCUMENTS_ACCEPT_FORMATS}
        onFilesAccepted={handleFilesAccepted}
        onFilesRejected={onFilesRejected}
        className="size-full"
      >
        <div className="flex flex-col items-center justify-center">
          <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-background-secondary">
            <ArrowUpToLine className="text-text-primary" size={24} />
          </div>
          <TypographyP2 className="font-medium text-text-primary">
            {t('library.wizard.entry.dragDropLabel' as TranslationKey)}
          </TypographyP2>
          <TypographyCaption className="mt-1 text-text-tertiary">
            {t('library.wizard.entry.supportedFormats' as TranslationKey)}
          </TypographyCaption>
        </div>
      </FileDropzone>

      {/* Error message */}
      {error && (
        <TypographyCaption className="text-validation-destructive">
          {error}
        </TypographyCaption>
      )}
    </div>
  );
};
